import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { colaboradoresAPI } from '../../lib/api';

const emptyForm = {
  numero_nomina: '', nombre_completo: '',
  fecha_nacimiento: '', lugar_nacimiento: '', nss: '', rfc: '', curp: '', sexo: '',
  estado_civil: '', escolaridad: '',
  cp_fiscal: '', domicilio_fiscal: '', cp_actual: '', domicilio_actual: '',
  correo_electronico: '', telefono: '',
  ce_nombre: '', ce_parentesco: '', ce_telefono: '',
  numero_hijos: 0, talla_calzado: '', talla_playera: '', alergias: '',
  cuenta_bancaria: '', infonavit_numero_credito: '', infonavit_tipo_descuento: '',
  b1_nombre: '', b1_parentesco: '', b1_fecha_nacimiento: '', b1_porcentaje: '',
  b2_nombre: '', b2_parentesco: '', b2_fecha_nacimiento: '', b2_porcentaje: '',
  numero_locker: '', candado_empresa: '',
  clasificacion: '', departamento: '', puesto: '', fecha_alta: '', salario_diario: '',
  sueldo: '', sd_fiscal: '', temporalidad_contrato: '', fecha_termino_contrato: '',
  estatus_colaborador: 'Activo',
};

const ESTATUS_OPCIONES = ['Prueba', 'Activo', 'Incapacidad', 'Inactivo'];

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-3 mt-2">{children}</h3>
);

const Field = ({ k, label, type = 'text', required, placeholder, maxLength, uppercase, showCount, form, errors, set }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
    <Input
      type={type} value={form[k]} maxLength={maxLength}
      placeholder={placeholder}
      onChange={(ev) => set(k, uppercase ? ev.target.value.toUpperCase() : ev.target.value)}
      className={errors[k] ? 'border-red-400' : ''}
      data-testid={`colab-field-${k}`}
    />
    {errors[k]
      ? <p className="text-xs text-red-500 mt-0.5">{errors[k]}</p>
      : showCount && maxLength
        ? <p className={`text-xs mt-0.5 ${((form[k] || '').length === maxLength) ? 'text-emerald-600' : 'text-slate-400'}`}>{(form[k] || '').length}/{maxLength}</p>
        : null}
  </div>
);

const validate = (f, isEdit, activeEditable) => {
  const e = {};
  const req = (k) => { if (!String(f[k] ?? '').trim()) e[k] = 'Requerido'; };
  // Identidad
  req('nombre_completo');
  req('fecha_nacimiento'); req('lugar_nacimiento'); req('sexo');
  if (!f.curp.trim()) e.curp = 'Requerido';
  else if (f.curp.trim().length !== 18) e.curp = 'La CURP debe tener 18 caracteres';
  if (!f.rfc.trim()) e.rfc = 'Requerido';
  else if (!(f.rfc.trim().length >= 12 && f.rfc.trim().length <= 13)) e.rfc = 'RFC de 12 o 13 caracteres';
  if (!f.nss.trim()) e.nss = 'Requerido';
  else if (!/^\d{11}$/.test(f.nss.trim())) e.nss = 'El NSS debe tener 11 dígitos';
  // Contacto y datos personales
  if (!f.correo_electronico.trim()) e.correo_electronico = 'Requerido';
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.correo_electronico.trim())) e.correo_electronico = 'Correo inválido';
  req('telefono'); req('cp_fiscal'); req('domicilio_fiscal');
  req('ce_nombre'); req('ce_parentesco'); req('ce_telefono');
  req('talla_calzado'); req('talla_playera'); req('alergias');
  if (f.numero_hijos === '' || f.numero_hijos === null || f.numero_hijos === undefined) e.numero_hijos = 'Requerido';
  // Periodo laboral (alta o periodo activo editable)
  if (!isEdit || activeEditable) {
    req('clasificacion'); req('departamento'); req('puesto');
    if (!f.fecha_alta) e.fecha_alta = 'Requerido';
    if (f.salario_diario === '' || f.salario_diario === null || f.salario_diario === undefined) e.salario_diario = 'Requerido';
  }
  return e;
};

export default function ColaboradorFormDialog({ open, onClose, onSaved, colaborador }) {
  const isEdit = !!colaborador;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const activePeriod = useMemo(() => {
    const periods = colaborador?.periodos_laborales || [];
    const last = periods[periods.length - 1];
    return last && !last.fecha_baja ? last : null;
  }, [colaborador]);
  const activeEditable = !!activePeriod;

  useEffect(() => {
    if (!open) return;
    if (colaborador) {
      const ce = colaborador.contacto_emergencia || {};
      const b1 = colaborador.beneficiario_1 || {};
      const b2 = colaborador.beneficiario_2 || {};
      setForm({
        ...emptyForm,
        numero_nomina: colaborador.numero_nomina || '',
        nombre_completo: colaborador.nombre_completo || '',
        fecha_nacimiento: colaborador.fecha_nacimiento || '',
        lugar_nacimiento: colaborador.lugar_nacimiento || '',
        nss: colaborador.nss || '', rfc: colaborador.rfc || '', curp: colaborador.curp || '',
        sexo: colaborador.sexo || '',
        estado_civil: colaborador.estado_civil || '', escolaridad: colaborador.escolaridad || '',
        cp_fiscal: colaborador.cp_fiscal || '',
        domicilio_fiscal: colaborador.domicilio_fiscal || '',
        cp_actual: colaborador.cp_actual || '', domicilio_actual: colaborador.domicilio_actual || '',
        correo_electronico: colaborador.correo_electronico || '',
        telefono: colaborador.telefono || '',
        ce_nombre: ce.nombre || '', ce_parentesco: ce.parentesco || '', ce_telefono: ce.telefono || '',
        numero_hijos: colaborador.numero_hijos || 0,
        talla_calzado: colaborador.talla_calzado || '', talla_playera: colaborador.talla_playera || '',
        alergias: colaborador.alergias || '',
        cuenta_bancaria: colaborador.cuenta_bancaria || '',
        infonavit_numero_credito: colaborador.infonavit_numero_credito || '',
        infonavit_tipo_descuento: colaborador.infonavit_tipo_descuento || '',
        b1_nombre: b1.nombre || '', b1_parentesco: b1.parentesco || '',
        b1_fecha_nacimiento: b1.fecha_nacimiento || '', b1_porcentaje: b1.porcentaje ?? '',
        b2_nombre: b2.nombre || '', b2_parentesco: b2.parentesco || '',
        b2_fecha_nacimiento: b2.fecha_nacimiento || '', b2_porcentaje: b2.porcentaje ?? '',
        numero_locker: colaborador.numero_locker || '', candado_empresa: colaborador.candado_empresa || '',
        clasificacion: activePeriod?.clasificacion || '',
        departamento: activePeriod?.departamento || '',
        puesto: activePeriod?.puesto || '',
        fecha_alta: activePeriod?.fecha_alta || '',
        salario_diario: activePeriod?.salario_diario ?? '',
        sueldo: activePeriod?.sueldo ?? '',
        sd_fiscal: activePeriod?.sd_fiscal ?? '',
        temporalidad_contrato: activePeriod?.temporalidad_contrato || '',
        fecha_termino_contrato: activePeriod?.fecha_termino_contrato || '',
        estatus_colaborador: activePeriod?.estatus_colaborador || 'Activo',
        _sd_fiscal_con_letra: activePeriod?.sd_fiscal_con_letra || '',
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [open, colaborador, activePeriod]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    const eMap = validate(form, isEdit, activeEditable);
    setErrors(eMap);
    if (Object.keys(eMap).length > 0) {
      toast.error('Revisa los campos marcados en rojo');
      return;
    }
    setSaving(true);
    try {
      const contacto = (form.ce_nombre || form.ce_parentesco || form.ce_telefono)
        ? { nombre: form.ce_nombre || null, parentesco: form.ce_parentesco || null, telefono: form.ce_telefono || null }
        : null;
      const buildBenef = (n) => {
        const nom = form[`b${n}_nombre`], par = form[`b${n}_parentesco`],
          fn = form[`b${n}_fecha_nacimiento`], pct = form[`b${n}_porcentaje`];
        if (!nom && !par && !fn && pct === '') return null;
        return {
          nombre: nom || null, parentesco: par || null,
          fecha_nacimiento: fn || null,
          porcentaje: pct === '' ? null : parseFloat(pct),
        };
      };
      const beneficiario_1 = buildBenef(1);
      const beneficiario_2 = buildBenef(2);
      // Validación suave: porcentajes de beneficiarios no deben exceder 100% combinado
      const p1 = beneficiario_1?.porcentaje || 0;
      const p2 = beneficiario_2?.porcentaje || 0;
      if (p1 + p2 > 100) {
        toast.warning(`Los porcentajes de beneficiarios suman ${p1 + p2}% (excede 100%). Revisa los valores.`);
      }
      const masterExtra = {
        estado_civil: form.estado_civil || null, escolaridad: form.escolaridad || null,
        cp_actual: form.cp_actual || null, domicilio_actual: form.domicilio_actual || null,
        cuenta_bancaria: form.cuenta_bancaria || null,
        infonavit_numero_credito: form.infonavit_numero_credito || null,
        infonavit_tipo_descuento: form.infonavit_tipo_descuento || null,
        beneficiario_1, beneficiario_2,
        numero_locker: form.numero_locker || null, candado_empresa: form.candado_empresa || null,
      };
      const periodoExtra = {
        sueldo: form.sueldo === '' ? null : parseFloat(form.sueldo),
        sd_fiscal: form.sd_fiscal === '' ? null : parseFloat(form.sd_fiscal),
        temporalidad_contrato: form.temporalidad_contrato || null,
        fecha_termino_contrato: form.fecha_termino_contrato || null,
        estatus_colaborador: form.estatus_colaborador || 'Activo',
      };
      if (isEdit) {
        const payload = {
          nombre_completo: form.nombre_completo,
          fecha_nacimiento: form.fecha_nacimiento || null, lugar_nacimiento: form.lugar_nacimiento || null,
          nss: form.nss || null, rfc: form.rfc || null, sexo: form.sexo || null,
          cp_fiscal: form.cp_fiscal || null, domicilio_fiscal: form.domicilio_fiscal || null,
          correo_electronico: form.correo_electronico || null, telefono: form.telefono || null,
          contacto_emergencia: contacto,
          numero_hijos: parseInt(form.numero_hijos) || 0,
          talla_calzado: form.talla_calzado || null, talla_playera: form.talla_playera || null,
          alergias: form.alergias || null,
          ...masterExtra,
        };
        if (activeEditable) {
          payload.periodo_activo = {
            clasificacion: form.clasificacion || null,
            departamento: form.departamento, puesto: form.puesto,
            fecha_alta: form.fecha_alta,
            salario_diario: form.salario_diario === '' ? null : parseFloat(form.salario_diario),
            ...periodoExtra,
          };
        }
        await colaboradoresAPI.update(colaborador.id, payload);
        toast.success('Colaborador actualizado');
      } else {
        const payload = {
          nombre_completo: form.nombre_completo,
          fecha_nacimiento: form.fecha_nacimiento || null, lugar_nacimiento: form.lugar_nacimiento || null,
          nss: form.nss || null, rfc: form.rfc || null, curp: form.curp.toUpperCase(), sexo: form.sexo || null,
          cp_fiscal: form.cp_fiscal || null, domicilio_fiscal: form.domicilio_fiscal || null,
          correo_electronico: form.correo_electronico || null, telefono: form.telefono || null,
          contacto_emergencia: contacto, numero_hijos: parseInt(form.numero_hijos) || 0,
          talla_calzado: form.talla_calzado || null, talla_playera: form.talla_playera || null,
          alergias: form.alergias || null,
          ...masterExtra,
          clasificacion: form.clasificacion || null, departamento: form.departamento, puesto: form.puesto,
          fecha_alta: form.fecha_alta,
          salario_diario: form.salario_diario === '' ? null : parseFloat(form.salario_diario),
          ...periodoExtra,
        };
        await colaboradoresAPI.create(payload);
        toast.success('Colaborador creado');
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="colab-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar colaborador' : 'Nuevo colaborador'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-1">
          <SectionTitle>Identidad</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {isEdit && (
              <div>
                <Label className="text-xs text-slate-600">Número de nómina (vigente)</Label>
                <Input value={colaborador?.numero_nomina || ''} disabled className="bg-slate-100 text-slate-500" data-testid="colab-field-numero_nomina" />
              </div>
            )}
            <div className="sm:col-span-3">
              <Field form={form} errors={errors} set={set} k="nombre_completo" label="Nombre completo" required />
            </div>
            <Field form={form} errors={errors} set={set} k="curp" label="CURP" required maxLength={18} uppercase showCount />
            <Field form={form} errors={errors} set={set} k="rfc" label="RFC" required maxLength={13} uppercase />
            <Field form={form} errors={errors} set={set} k="nss" label="NSS" required maxLength={11} />
            <Field form={form} errors={errors} set={set} k="fecha_nacimiento" label="Fecha de nacimiento" type="date" required />
            <Field form={form} errors={errors} set={set} k="lugar_nacimiento" label="Lugar de nacimiento" required />
            <div>
              <Label className="text-xs text-slate-600">Sexo<span className="text-red-500"> *</span></Label>
              <Select value={form.sexo || ''} onValueChange={(v) => set('sexo', v)}>
                <SelectTrigger data-testid="colab-field-sexo" className={errors.sexo ? 'border-red-400' : ''}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculino</SelectItem>
                  <SelectItem value="F">Femenino</SelectItem>
                  <SelectItem value="O">Otro</SelectItem>
                </SelectContent>
              </Select>
              {errors.sexo && <p className="text-xs text-red-500 mt-0.5">{errors.sexo}</p>}
            </div>
            <Field form={form} errors={errors} set={set} k="estado_civil" label="Estado civil" />
            <Field form={form} errors={errors} set={set} k="escolaridad" label="Escolaridad" />
          </div>

          <SectionTitle>Contacto y datos personales</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field form={form} errors={errors} set={set} k="correo_electronico" label="Correo electrónico" type="email" required />
            <Field form={form} errors={errors} set={set} k="telefono" label="Teléfono" required />
            <Field form={form} errors={errors} set={set} k="cp_fiscal" label="CP fiscal" required />
            <div className="sm:col-span-3"><Field form={form} errors={errors} set={set} k="domicilio_fiscal" label="Domicilio fiscal" required /></div>
            <Field form={form} errors={errors} set={set} k="cp_actual" label="CP actual" />
            <div className="sm:col-span-2"><Field form={form} errors={errors} set={set} k="domicilio_actual" label="Domicilio actual" /></div>
            <Field form={form} errors={errors} set={set} k="ce_nombre" label="Contacto emergencia (nombre)" required />
            <Field form={form} errors={errors} set={set} k="ce_parentesco" label="Parentesco" required />
            <Field form={form} errors={errors} set={set} k="ce_telefono" label="Teléfono emergencia" required />
            <Field form={form} errors={errors} set={set} k="numero_hijos" label="Número de hijos" type="number" required />
            <Field form={form} errors={errors} set={set} k="talla_calzado" label="Talla calzado" required />
            <Field form={form} errors={errors} set={set} k="talla_playera" label="Talla playera" required />
            <div className="sm:col-span-3">
              <Label className="text-xs text-slate-600">Alergias (medicamentos / alimentos)<span className="text-red-500"> *</span></Label>
              <Textarea value={form.alergias} onChange={(e) => set('alergias', e.target.value)} rows={2} data-testid="colab-field-alergias" className={errors.alergias ? 'border-red-400' : ''} />
              {errors.alergias && <p className="text-xs text-red-500 mt-0.5">{errors.alergias}</p>}
            </div>
          </div>

          <SectionTitle>Datos administrativos</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field form={form} errors={errors} set={set} k="cuenta_bancaria" label="Cuenta bancaria" />
            <Field form={form} errors={errors} set={set} k="infonavit_numero_credito" label="Infonavit - N° crédito" />
            <Field form={form} errors={errors} set={set} k="infonavit_tipo_descuento" label="Infonavit - Tipo descuento" />
            <Field form={form} errors={errors} set={set} k="numero_locker" label="Número de locker" />
            <Field form={form} errors={errors} set={set} k="candado_empresa" label="Candado empresa" />
          </div>

          <SectionTitle>Beneficiarios</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field form={form} errors={errors} set={set} k="b1_nombre" label="Beneficiario 1 (nombre)" />
            <Field form={form} errors={errors} set={set} k="b1_parentesco" label="Parentesco" />
            <Field form={form} errors={errors} set={set} k="b1_fecha_nacimiento" label="Fecha nacimiento" type="date" />
            <Field form={form} errors={errors} set={set} k="b1_porcentaje" label="Porcentaje (%)" type="number" />
            <Field form={form} errors={errors} set={set} k="b2_nombre" label="Beneficiario 2 (nombre)" />
            <Field form={form} errors={errors} set={set} k="b2_parentesco" label="Parentesco" />
            <Field form={form} errors={errors} set={set} k="b2_fecha_nacimiento" label="Fecha nacimiento" type="date" />
            <Field form={form} errors={errors} set={set} k="b2_porcentaje" label="Porcentaje (%)" type="number" />
          </div>

          <SectionTitle>{isEdit ? 'Periodo laboral actual' : 'Periodo laboral (alta)'}</SectionTitle>
          {isEdit && !activeEditable ? (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              El colaborador está en Baja. Usa "Reingreso" para agregar un nuevo periodo laboral.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field form={form} errors={errors} set={set} k="clasificacion" label="Clasificación" required />
              <Field form={form} errors={errors} set={set} k="departamento" label="Departamento" required />
              <Field form={form} errors={errors} set={set} k="puesto" label="Puesto" required />
              <Field form={form} errors={errors} set={set} k="fecha_alta" label="Fecha de alta" type="date" required />
              <Field form={form} errors={errors} set={set} k="salario_diario" label="Salario diario (MXN)" type="number" required />
              <Field form={form} errors={errors} set={set} k="sueldo" label="Sueldo mensual (MXN)" type="number" />
              <Field form={form} errors={errors} set={set} k="sd_fiscal" label="SD fiscal (MXN)" type="number" />
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-600">SD fiscal con letra (automático)</Label>
                <Input value={form._sd_fiscal_con_letra || ''} disabled readOnly placeholder="Se genera automáticamente al guardar" className="bg-slate-100 text-slate-500" data-testid="colab-field-sd_fiscal_con_letra" />
              </div>
              <Field form={form} errors={errors} set={set} k="temporalidad_contrato" label="Temporalidad de contrato" />
              <Field form={form} errors={errors} set={set} k="fecha_termino_contrato" label="Fecha término contrato" type="date" />
              <div>
                <Label className="text-xs text-slate-600">Estatus colaborador</Label>
                <Select value={form.estatus_colaborador || 'Activo'} onValueChange={(v) => set('estatus_colaborador', v)}>
                  <SelectTrigger data-testid="colab-field-estatus_colaborador"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTATUS_OPCIONES.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="colab-form-cancel">Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="colab-form-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear colaborador'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
